import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, GripVertical, Clock, MessageSquare, Send, UserCheck, ChevronDown, ChevronUp, User, Shield, Trash2, FolderOpen, TrendingUp, FileText, ExternalLink, CheckCircle, Circle, Search, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = {
  todo: { id: 'todo', title: 'To Do', color: 'bg-slate-100', borderColor: 'border-slate-300', textColor: 'text-slate-700', icon: Circle },
  in_progress: { id: 'in_progress', title: 'In Progress', color: 'bg-blue-50', borderColor: 'border-blue-300', textColor: 'text-blue-700', icon: Clock },
  done: { id: 'done', title: 'Done', color: 'bg-green-50', borderColor: 'border-green-300', textColor: 'text-green-700', icon: CheckCircle }
};

const AdminTaskBoard = ({ cases = [] }) => {
  const [caseTasks, setCaseTasks] = useState({});
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cases'); // 'cases' or 'kanban'
  const [expandedCases, setExpandedCases] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [newComment, setNewComment] = useState('');
  const [activeCommentTask, setActiveCommentTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');

  const loadAllCaseTasks = async () => {
    setLoading(true);
    const tasksByCase = {};
    const allTasksList = [];

    for (const caseItem of cases) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/case-tasks/${caseItem.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const tasksWithCaseInfo = data.map(t => ({
            ...t,
            case_id: caseItem.id,
            case_name: caseItem.company_name,
            case_status: caseItem.status,
            client_name: caseItem.client_name
          }));
          tasksByCase[caseItem.id] = tasksWithCaseInfo;
          allTasksList.push(...tasksWithCaseInfo);
        }
      } catch (error) {
        console.error(`Failed to load tasks for case ${caseItem.id}:`, error);
      }
    }
    setCaseTasks(tasksByCase);
    setAllTasks(allTasksList);
    setLoading(false);
  };

  useEffect(() => {
    if (cases.length > 0) {
      loadAllCaseTasks();
    } else {
      setLoading(false);
    }
  }, [cases]); // eslint-disable-line

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = destination.droppableId;
    const oldStatus = source.droppableId;

    // Update local state
    setAllTasks(prev =>
      prev.map(t => t.id === draggableId ? { ...t, status: newStatus } : t)
    );

    // Update caseTasks state
    const task = allTasks.find(t => t.id === draggableId);
    if (task) {
      setCaseTasks(prev => ({
        ...prev,
        [task.case_id]: prev[task.case_id]?.map(t => 
          t.id === draggableId ? { ...t, status: newStatus } : t
        )
      }));
    }

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/case-tasks/${draggableId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Task moved to ${COLUMNS[newStatus]?.title}`);
    } catch (error) {
      // Revert on error
      setAllTasks(prev =>
        prev.map(t => t.id === draggableId ? { ...t, status: oldStatus } : t)
      );
      if (task) {
        setCaseTasks(prev => ({
          ...prev,
          [task.case_id]: prev[task.case_id]?.map(t => 
            t.id === draggableId ? { ...t, status: oldStatus } : t
          )
        }));
      }
      toast.error('Failed to update task status');
    }
  };

  const handleAddComment = async (taskId) => {
    if (!newComment.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/case-tasks/${taskId}/comments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newComment })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setAllTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updatedTask } : t));
        const task = allTasks.find(t => t.id === taskId);
        if (task) {
          setCaseTasks(prev => ({
            ...prev,
            [task.case_id]: prev[task.case_id]?.map(t => 
              t.id === taskId ? { ...t, ...updatedTask } : t
            )
          }));
        }
        setNewComment('');
        setActiveCommentTask(null);
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleReassign = async (taskId, newAssignee) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/case-tasks/${taskId}/reassign?assignee=${newAssignee}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setAllTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updatedTask, assignee: newAssignee } : t));
        const task = allTasks.find(t => t.id === taskId);
        if (task) {
          setCaseTasks(prev => ({
            ...prev,
            [task.case_id]: prev[task.case_id]?.map(t => 
              t.id === taskId ? { ...t, ...updatedTask, assignee: newAssignee } : t
            )
          }));
        }
        toast.success(`Task reassigned to ${newAssignee === 'admin' ? 'Admin' : 'Client'}`);
      }
    } catch (error) {
      toast.error('Failed to reassign task');
    }
  };

  const toggleCaseExpand = (caseId) => {
    setExpandedCases(prev => ({ ...prev, [caseId]: !prev[caseId] }));
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getCaseProgress = (caseId) => {
    const cTasks = caseTasks[caseId] || [];
    if (cTasks.length === 0) return { total: 0, done: 0, percentage: 0, inProgress: 0, todo: 0 };
    const done = cTasks.filter(t => t.status === 'done').length;
    return {
      total: cTasks.length,
      done,
      inProgress: cTasks.filter(t => t.status === 'in_progress').length,
      todo: cTasks.filter(t => t.status === 'todo').length,
      percentage: Math.round((done / cTasks.length) * 100)
    };
  };

  const getStatusCounts = () => {
    const counts = { todo: [], in_progress: [], done: [] };
    allTasks.forEach(t => {
      if (counts[t.status]) {
        counts[t.status].push({
          taskId: t.id,
          caseId: t.case_id,
          caseName: t.case_name,
          taskTitle: t.title,
          assignee: t.assignee
        });
      }
    });
    return counts;
  };

  const getFilteredTasks = () => {
    return allTasks.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            task.case_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            task.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
      const matchesAssignee = filterAssignee === 'all' || task.assignee === filterAssignee;
      return matchesSearch && matchesStatus && matchesAssignee;
    });
  };

  const getTasksByStatus = (status) => {
    return getFilteredTasks().filter(task => task.status === status);
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[priority] || colors.medium;
  };

  const filteredCases = cases.filter(c => 
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading tasks across all cases...</div>;
  }

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">All Case Tasks</h3>
          <p className="text-sm text-slate-500">Manage tasks across all cases • {allTasks.length} total tasks in {cases.length} cases</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cases')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'cases' 
                  ? 'bg-white text-purple-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FolderOpen size={16} className="inline mr-2" />
              By Case
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'kanban' 
                  ? 'bg-white text-purple-700 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <TrendingUp size={16} className="inline mr-2" />
              Kanban
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search cases or tasks..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
          />
        </div>
        {viewMode === 'kanban' && (
          <>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="all">All Status</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
            </select>
            <select
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="all">All Assignees</option>
              <option value="admin">Admin Tasks</option>
              <option value="client">Client Tasks</option>
            </select>
          </>
        )}
      </div>

      {/* Status Overview Cards */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(COLUMNS).map(([status, column]) => {
          const Icon = column.icon;
          const count = statusCounts[status]?.length || 0;
          const adminCount = statusCounts[status]?.filter(t => t.assignee === 'admin').length || 0;
          const clientCount = statusCounts[status]?.filter(t => t.assignee === 'client').length || 0;
          return (
            <div
              key={status}
              className={`${column.color} border-2 ${column.borderColor} rounded-xl p-4 cursor-pointer hover:shadow-md transition-all`}
              onClick={() => setViewMode('kanban')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={20} className={column.textColor} />
                  <span className={`font-semibold ${column.textColor}`}>{column.title}</span>
                </div>
                <span className={`text-2xl font-bold ${column.textColor}`}>{count}</span>
              </div>
              <div className="flex gap-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Shield size={12} className="text-purple-600" /> {adminCount}
                </span>
                <span className="flex items-center gap-1">
                  <User size={12} className="text-blue-600" /> {clientCount}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {new Set(statusCounts[status]?.map(t => t.caseId)).size} cases
              </p>
            </div>
          );
        })}
      </div>

      {/* Cases View */}
      {viewMode === 'cases' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-slate-900">Cases & Task Progress</h4>
            <span className="text-sm text-slate-500">{filteredCases.length} cases</span>
          </div>
          
          {filteredCases.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FolderOpen className="mx-auto text-slate-400 mb-4" size={48} />
              <p className="text-slate-600">No cases found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCases.map((caseItem) => {
                const progress = getCaseProgress(caseItem.id);
                const cTasks = caseTasks[caseItem.id] || [];
                const isExpanded = expandedCases[caseItem.id];
                
                return (
                  <div key={caseItem.id} className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden hover:border-purple-300 transition-all">
                    {/* Case Header */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => toggleCaseExpand(caseItem.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            progress.percentage === 100 ? 'bg-green-100' : 
                            progress.percentage > 0 ? 'bg-blue-100' : 'bg-slate-100'
                          }`}>
                            <FileText size={20} className={
                              progress.percentage === 100 ? 'text-green-600' : 
                              progress.percentage > 0 ? 'text-blue-600' : 'text-slate-600'
                            } />
                          </div>
                          <div>
                            <h5 className="font-semibold text-slate-900">{caseItem.company_name}</h5>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span>Client: {caseItem.client_name}</span>
                              <span>•</span>
                              <span className={`px-2 py-0.5 rounded-full ${
                                caseItem.status === 'resolved' ? 'bg-green-100 text-green-700' :
                                caseItem.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {caseItem.status?.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Progress Bar */}
                          <div className="w-32 hidden sm:block">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>{progress.done}/{progress.total} tasks</span>
                              <span>{progress.percentage}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all ${
                                  progress.percentage === 100 ? 'bg-green-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${progress.percentage}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Task Status Summary */}
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                              {progress.todo} todo
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                              {progress.inProgress} active
                            </span>
                            <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">
                              {progress.done} done
                            </span>
                          </div>
                          
                          <ChevronDown 
                            size={20} 
                            className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Task List */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 bg-slate-50 p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h6 className="font-semibold text-slate-700">Case Tasks</h6>
                          <Link 
                            to={`/case/${caseItem.id}`}
                            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                          >
                            View Full Case <ExternalLink size={14} />
                          </Link>
                        </div>
                        
                        {cTasks.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-4">No tasks for this case yet</p>
                        ) : (
                          <div className="space-y-2">
                            {cTasks.sort((a, b) => (a.order || 99) - (b.order || 99)).map((task, idx) => (
                              <div 
                                key={task.id} 
                                className={`flex items-center gap-3 p-3 rounded-lg border ${
                                  task.status === 'done' ? 'bg-green-50 border-green-200' :
                                  task.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                                  'bg-white border-slate-200'
                                }`}
                              >
                                <span className="text-xs text-slate-400 w-6">{idx + 1}.</span>
                                {task.status === 'done' ? (
                                  <CheckCircle size={18} className="text-green-500" />
                                ) : task.status === 'in_progress' ? (
                                  <Clock size={18} className="text-blue-500" />
                                ) : (
                                  <Circle size={18} className="text-slate-400" />
                                )}
                                {task.assignee === 'admin' ? (
                                  <Shield size={14} className="text-purple-600" />
                                ) : (
                                  <User size={14} className="text-blue-600" />
                                )}
                                <div className="flex-1">
                                  <span className={`text-sm ${task.status === 'done' ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                                    {task.title}
                                  </span>
                                  {task.description && (
                                    <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>
                                  )}
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  task.status === 'done' ? 'bg-green-100 text-green-700' :
                                  task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {task.status === 'done' ? 'Completed' : 
                                   task.status === 'in_progress' ? 'In Progress' : 'To Do'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Kanban Board View */}
      {viewMode === 'kanban' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.values(COLUMNS).map((column) => (
              <Droppable key={column.id} droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`rounded-xl p-4 min-h-[500px] border-2 transition-all ${column.color} ${
                      snapshot.isDraggingOver ? 'border-purple-400 bg-purple-50' : column.borderColor
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`font-bold text-lg ${column.textColor}`}>
                        {column.title}
                      </h4>
                      <span className={`text-sm font-semibold px-3 py-1 rounded-full ${column.color} ${column.textColor} border ${column.borderColor}`}>
                        {getTasksByStatus(column.id).length}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {getTasksByStatus(column.id).map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden ${
                                snapshot.isDragging ? 'shadow-xl ring-2 ring-purple-400 border-purple-400' : 
                                task.assignee === 'admin' ? 'border-purple-200 hover:border-purple-300' : 'border-blue-200 hover:border-blue-300'
                              }`}
                            >
                              {/* Task Header */}
                              <div className="p-3">
                                <div className="flex items-start gap-2">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="text-slate-400 hover:text-purple-600 cursor-grab mt-1"
                                  >
                                    <GripVertical size={14} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      {task.assignee === 'admin' ? (
                                        <Shield size={12} className="text-purple-600" />
                                      ) : (
                                        <User size={12} className="text-blue-600" />
                                      )}
                                      <h5 className="font-semibold text-slate-900 text-sm line-clamp-1">{task.title}</h5>
                                    </div>
                                    
                                    {/* Case Info */}
                                    <Link 
                                      to={`/case/${task.case_id}`}
                                      className="inline-block mb-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                    >
                                      {task.case_name}
                                    </Link>
                                    
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityBadge(task.priority)}`}>
                                        {task.priority}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        task.assignee === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {task.assignee === 'admin' ? 'Admin' : 'Client'}
                                      </span>
                                    </div>

                                    {/* Task Meta */}
                                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                      <span className="flex items-center gap-1">
                                        <Clock size={10} />
                                        {task.updated_at ? format(new Date(task.updated_at), 'MMM d') : 'N/A'}
                                      </span>
                                      {task.comments?.length > 0 && (
                                        <span className="flex items-center gap-1">
                                          <MessageSquare size={10} />
                                          {task.comments.length}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => toggleTaskExpand(task.id)}
                                    className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                                  >
                                    {expandedTasks[task.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Section */}
                              {expandedTasks[task.id] && (
                                <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-3">
                                  {/* Description */}
                                  {task.description && (
                                    <p className="text-xs text-slate-600">{task.description}</p>
                                  )}
                                  
                                  {/* Comments */}
                                  <div>
                                    <h6 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                                      <MessageSquare size={12} />
                                      Comments ({task.comments?.length || 0})
                                    </h6>
                                    
                                    {task.comments?.length > 0 && (
                                      <div className="space-y-2 mb-2 max-h-24 overflow-y-auto">
                                        {task.comments.slice(-3).map((comment, idx) => (
                                          <div key={idx} className="bg-white rounded p-2 text-xs border border-slate-200">
                                            <div className="flex justify-between items-start">
                                              <span className="font-medium text-slate-800">{comment.author_name}</span>
                                              <span className="text-slate-400">
                                                {format(new Date(comment.created_at), 'MMM d')}
                                              </span>
                                            </div>
                                            <p className="text-slate-600 mt-1">{comment.content}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {/* Add Comment */}
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={activeCommentTask === task.id ? newComment : ''}
                                        onChange={(e) => {
                                          setActiveCommentTask(task.id);
                                          setNewComment(e.target.value);
                                        }}
                                        onFocus={() => setActiveCommentTask(task.id)}
                                        placeholder="Add comment..."
                                        className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:ring-1 focus:ring-purple-500 outline-none"
                                      />
                                      <button
                                        onClick={() => handleAddComment(task.id)}
                                        className="px-2 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700"
                                      >
                                        <Send size={12} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex gap-2 pt-2 border-t border-slate-200">
                                    <button
                                      onClick={() => handleReassign(task.id, task.assignee === 'admin' ? 'client' : 'admin')}
                                      className="flex-1 py-1.5 text-xs border border-slate-300 text-slate-700 rounded hover:bg-slate-100 flex items-center justify-center gap-1"
                                    >
                                      <UserCheck size={12} />
                                      Reassign to {task.assignee === 'admin' ? 'Client' : 'Admin'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {getTasksByStatus(column.id).length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          <p>No tasks in {column.title.toLowerCase()}</p>
                          <p className="text-xs mt-1">Drag tasks here</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      )}
    </div>
  );
};

export default AdminTaskBoard;
